import React, { useCallback, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, UploadCloud, FileText, AlertTriangle, CheckCircle2, ArrowRight, Download } from "lucide-react";
import * as XLSX from "xlsx";
import { parseTRDate, parseTRAmount, normalizeSheetDates } from "@/lib/excelDate";

// Beklenen sütun sırası (0-based indeks):
//   A(0)=Yön (Alınan/Verilen), B(1)=Çek No, C(2)=Banka, D(3)=Şube,
//   E(4)=Tutar, F(5)=Düzenleme Tarihi, G(6)=Vade Tarihi,
//   H(7)=Cari (opsiyonel), I(8)=Notlar (opsiyonel)
const COLS = {
  direction: 0, cheque_number: 1, bank_name: 2, branch: 3,
  amount: 4, issue_date: 5, due_date: 6,
  contact_name: 7, notes: 8,
};

const DIR_LABEL = { received: "Alınan", issued: "Verilen" };

const stripApos = (v) => String(v ?? "").replace(/^'+/, "").trim();

const normDirection = (s) => {
  const t = String(s || "").trim().toLowerCase();
  if (["alınan", "alinan", "received", "a", "gelen", "müşteri", "musteri"].includes(t)) return "received";
  if (["verilen", "issued", "v", "verdiğimiz", "verdigimiz", "giden", "tedarikçi", "tedarikci"].includes(t)) return "issued";
  return "";
};

function downloadTemplate() {
  const wb = XLSX.utils.book_new();
  const headers = [
    "Yön (Alınan/Verilen)", "Çek No", "Banka", "Şube (opsiyonel)",
    "Tutar (TL)", "Düzenleme Tarihi (GG.AA.YYYY)", "Vade Tarihi (GG.AA.YYYY)",
    "Cari (opsiyonel)", "Notlar (opsiyonel)",
  ];
  const sample = [
    ["Alınan", "1234567", "Ziraat Bankası", "Merkez", "15000,00", "01.02.2026", "01.05.2026", "ABC Ticaret Ltd.", "Örnek — sil"],
    ["Verilen", "9876543", "Garanti BBVA", "Ataşehir", "8500,50", "10.02.2026", "10.04.2026", "XYZ Tedarik A.Ş.", ""],
  ];
  const ws = XLSX.utils.aoa_to_sheet([headers, ...sample]);
  ws["!cols"] = headers.map(() => ({ wch: 22 }));
  XLSX.utils.book_append_sheet(wb, ws, "Çekler");
  XLSX.writeFile(wb, "cek-sablonu.xlsx");
}

export default function ChequeImportModal({ defaultDirection, onClose, onImported }) {
  const [step, setStep] = useState("upload");
  const [rows, setRows] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const fileInputRef = useRef(null);

  const handleFile = useCallback((file) => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = new Uint8Array(ev.target.result);
        // raw:true prevents XLSX from auto-interpreting "06.08.2026" TR-format
        // CSV date strings as US MM/DD dates. Excel-native numeric date cells
        // are still converted to TR strings via normalizeSheetDates below.
        const wb = XLSX.read(data, { type: "array", codepage: 65001, raw: true, cellNF: true });
        const ws = wb.Sheets[wb.SheetNames[0]];
        normalizeSheetDates(ws);
        const arr = XLSX.utils.sheet_to_json(ws, { header: 1, blankrows: false, defval: "", raw: false, dateNF: "dd.mm.yyyy" });
        if (!arr.length) { toast.error("Dosya boş"); return; }

        // Başlık satırı algıla
        let startIdx = 0;
        const first = (arr[0] || []).map((x) => String(x || "").toLowerCase());
        if (first.some((c) => c.includes("çek no") || c.includes("cek no") || c.includes("yön") || c.includes("yon"))) {
          startIdx = 1;
        }

        const parsed = [];
        for (let i = startIdx; i < arr.length; i++) {
          const r = arr[i];
          const num = stripApos(r[COLS.cheque_number]);
          if (!num) continue;

          const dirRaw = String(r[COLS.direction] || (defaultDirection === "issued" ? "Verilen" : "Alınan")).trim();
          const direction = normDirection(dirRaw);
          const bank = String(r[COLS.bank_name] || "").trim();
          const branch = String(r[COLS.branch] || "").trim();
          const amountRaw = r[COLS.amount];
          const issueRaw = r[COLS.issue_date];
          const dueRaw = r[COLS.due_date];
          const contact = String(r[COLS.contact_name] || "").trim();
          const notes = String(r[COLS.notes] || "").trim();

          const amt = parseTRAmount(amountRaw);
          const iso = parseTRDate(issueRaw);
          const due = parseTRDate(dueRaw);

          let err = "";
          if (!direction) err = "Yön belirsiz (Alınan/Verilen)";
          else if (!bank) err = "Banka boş";
          else if (!iso) err = "Düzenleme tarihi geçersiz";
          else if (!due) err = "Vade tarihi geçersiz";
          else if (isNaN(amt) || amt <= 0) err = "Tutar geçersiz";

          parsed.push({
            direction,
            direction_raw: dirRaw,
            cheque_number: num,
            bank_name: bank,
            branch,
            amount: isNaN(amt) ? null : amt,
            amount_raw: String(amountRaw || ""),
            issue_date: iso, issue_date_raw: String(issueRaw || ""),
            due_date: due, due_date_raw: String(dueRaw || ""),
            contact_name: contact,
            notes,
            _error: err,
          });
        }
        if (!parsed.length) { toast.error("Aktarılabilecek satır bulunamadı"); return; }
        setRows(parsed);
        setStep("preview");
      } catch (e) {
        console.error(e);
        toast.error("Dosya okunamadı: " + (e?.message || ""));
      }
    };
    reader.onerror = () => toast.error("Dosya okunamadı");
    reader.readAsArrayBuffer(file);
  }, [defaultDirection]);

  const stats = useMemo(() => {
    const total = rows.length;
    const invalid = rows.filter((r) => r._error).length;
    const received = rows.filter((r) => !r._error && r.direction === "received").length;
    const issued = rows.filter((r) => !r._error && r.direction === "issued").length;
    const totalAmount = rows.reduce((s, r) => s + (r._error ? 0 : Number(r.amount) || 0), 0);
    return { total, valid: total - invalid, invalid, received, issued, totalAmount };
  }, [rows]);

  const submit = async () => {
    const items = rows.filter((r) => !r._error).map((r) => ({
      direction: r.direction,
      cheque_number: r.cheque_number,
      bank_name: r.bank_name,
      branch: r.branch || null,
      amount: r.amount,
      issue_date: r.issue_date,
      due_date: r.due_date,
      contact_name: r.contact_name || null,
      notes: r.notes || null,
    }));
    if (!items.length) { toast.error("Aktarılacak geçerli satır yok"); return; }
    setUploading(true);
    try {
      const { data } = await api.post("/cheques/bulk-import", { items });
      setResult(data);
      setStep("result");
      toast.success(`${data.imported} çek aktarıldı`);
      onImported?.();
    } catch (e) {
      toast.error("Aktarım hatası: " + (e?.response?.data?.detail || e?.message || ""));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="cheque-import-modal">
      <div className="brutal-card bg-white w-full max-w-5xl max-h-[92vh] flex flex-col">
        <div className="flex items-center justify-between border-b-2 border-black px-5 py-3">
          <div className="flex items-center gap-2">
            <FileText size={18} />
            <h2 className="font-display text-xl font-bold">Çekleri İçe Aktar (Alınan / Verilen)</h2>
          </div>
          <button onClick={onClose} className="p-1 hover:bg-black/5" data-testid="cheque-import-close">
            <X size={18} />
          </button>
        </div>

        {step === "upload" && (
          <div className="p-6 space-y-4">
            <div
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) handleFile(f); }}
              className="border-2 border-dashed border-black/30 p-10 text-center cursor-pointer hover:bg-neutral-50"
              data-testid="cheque-import-dropzone"
            >
              <UploadCloud size={44} className="mx-auto text-neutral-500" />
              <p className="mt-3 font-semibold">Excel veya CSV dosyası seçin ya da sürükleyin</p>
              <p className="text-xs text-neutral-500 mt-1">.xlsx / .xls / .csv</p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv,.xlsx,.xls,text/csv,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
              className="hidden"
              onChange={(e) => handleFile(e.target.files?.[0])}
              data-testid="cheque-import-file"
            />

            <div className="flex items-center gap-2">
              <button
                onClick={downloadTemplate}
                data-testid="cheque-import-template"
                className="brutal-btn bg-white text-black border border-black px-3 py-1.5 text-xs flex items-center gap-1.5"
              >
                <Download size={12} /> Örnek Şablon İndir (.xlsx)
              </button>
              <span className="text-xs text-neutral-500">Önerilir: önce şablonu indir, doldur, yükle</span>
            </div>

            <div className="bg-neutral-50 border border-black/10 p-4 text-xs">
              <p className="font-semibold uppercase tracking-widest mb-2">Beklenen sütun düzeni (bu sırada)</p>
              <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-neutral-700">
                <div><b>A</b> — Yön (Alınan / Verilen)</div>
                <div><b>F</b> — Düzenleme Tarihi</div>
                <div><b>B</b> — Çek No</div>
                <div><b>G</b> — Vade Tarihi</div>
                <div><b>C</b> — Banka</div>
                <div><b>H</b> — Cari (ops.)</div>
                <div><b>D</b> — Şube (ops.)</div>
                <div><b>I</b> — Notlar (ops.)</div>
                <div><b>E</b> — Tutar</div>
                <div />
              </div>
              <p className="text-neutral-500 mt-2">
                Başlık satırı otomatik atlanır. Aynı Yön + Çek No mevcutsa atlanır. Cari adı verilirse
                (varsa) sistemde otomatik eşleştirilir. Türkçe tarih (17.07.2026) ve virgüllü tutar (15000,00) desteklenir.
                Tüm kayıtlar <b>Portföyde</b> statüsü ile içe aktarılır.
              </p>
            </div>
          </div>
        )}

        {step === "preview" && (
          <>
            <div className="p-4 border-b border-black/10 text-sm">
              <span className="label-caps mr-2">Okunan:</span>
              <span className="font-mono-num font-semibold">{stats.total}</span>
              <span className="mx-3 text-neutral-300">|</span>
              <span className="label-caps mr-2">Geçerli:</span>
              <span className="font-mono-num font-semibold text-emerald-700">{stats.valid}</span>
              <span className="mx-3 text-neutral-300">|</span>
              <span className="label-caps mr-2">Alınan:</span>
              <span className="font-mono-num font-semibold text-emerald-700">{stats.received}</span>
              <span className="mx-3 text-neutral-300">|</span>
              <span className="label-caps mr-2">Verilen:</span>
              <span className="font-mono-num font-semibold text-rose-600">{stats.issued}</span>
              {stats.invalid > 0 && (
                <>
                  <span className="mx-3 text-neutral-300">|</span>
                  <span className="label-caps mr-2">Hatalı:</span>
                  <span className="font-mono-num font-semibold text-rose-600">{stats.invalid}</span>
                </>
              )}
              <span className="mx-3 text-neutral-300">|</span>
              <span className="label-caps mr-2">Toplam Tutar:</span>
              <span className="font-mono-num font-semibold">
                {stats.totalAmount.toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺
              </span>
            </div>
            <div className="flex-1 overflow-auto">
              <table className="w-full text-xs" data-testid="cheque-import-preview-table">
                <thead className="bg-neutral-50 sticky top-0 z-10">
                  <tr className="border-b-2 border-black/80">
                    <th className="text-left px-3 py-2 label-caps">Yön</th>
                    <th className="text-left px-3 py-2 label-caps">Çek No</th>
                    <th className="text-left px-3 py-2 label-caps">Banka</th>
                    <th className="text-left px-3 py-2 label-caps">Şube</th>
                    <th className="text-right px-3 py-2 label-caps">Tutar</th>
                    <th className="text-left px-3 py-2 label-caps">Düzenleme</th>
                    <th className="text-left px-3 py-2 label-caps">Vade</th>
                    <th className="text-left px-3 py-2 label-caps">Cari</th>
                    <th className="text-left px-3 py-2 label-caps">Durum</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r, i) => (
                    <tr key={i} className={`border-b border-black/5 ${r._error ? "bg-rose-50" : ""}`}>
                      <td className={`px-3 py-1.5 font-semibold ${r.direction === "received" ? "text-emerald-700" : r.direction === "issued" ? "text-rose-600" : "text-rose-600"}`}>
                        {r.direction ? DIR_LABEL[r.direction] : <span>{r.direction_raw}</span>}
                      </td>
                      <td className="px-3 py-1.5 font-mono-num">{r.cheque_number}</td>
                      <td className="px-3 py-1.5">{r.bank_name || "—"}</td>
                      <td className="px-3 py-1.5">{r.branch || "—"}</td>
                      <td className="px-3 py-1.5 text-right font-mono-num">
                        {r.amount != null
                          ? Number(r.amount).toLocaleString("tr-TR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                          : <span className="text-rose-600">{r.amount_raw}</span>}
                      </td>
                      <td className="px-3 py-1.5 font-mono-num">{r.issue_date || <span className="text-rose-600">{r.issue_date_raw}</span>}</td>
                      <td className="px-3 py-1.5 font-mono-num">{r.due_date || <span className="text-rose-600">{r.due_date_raw}</span>}</td>
                      <td className="px-3 py-1.5">{r.contact_name || "—"}</td>
                      <td className="px-3 py-1.5">
                        {r._error
                          ? <span className="text-rose-700 text-[10px] uppercase tracking-widest flex items-center gap-1"><AlertTriangle size={10} /> {r._error}</span>
                          : <span className="text-emerald-700 text-[10px] uppercase tracking-widest">Hazır</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t border-black/10 p-4 flex items-center justify-between gap-2">
              <button
                onClick={() => { setRows([]); setStep("upload"); }}
                className="brutal-btn bg-white text-black border border-black px-4 py-2 text-sm"
                data-testid="cheque-import-back"
              >
                Geri
              </button>
              <button
                onClick={submit}
                disabled={uploading || stats.valid === 0}
                className="brutal-btn bg-black text-white px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-40"
                data-testid="cheque-import-submit"
              >
                {uploading ? "Aktarılıyor…" : `${stats.valid} Çeki Aktar`}
                <ArrowRight size={14} />
              </button>
            </div>
          </>
        )}

        {step === "result" && result && (
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-3">
              <CheckCircle2 size={40} className="text-emerald-600" />
              <div>
                <h3 className="font-display text-2xl font-bold">Aktarım Tamamlandı</h3>
                <p className="text-sm text-neutral-600">Sonuç aşağıda özetlenmiştir.</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <RStat label="Aktarılan" value={result.imported} tone="emerald" />
              <RStat label="Mükerrer" value={result.skipped_duplicates} />
              <RStat label="Cari Eşleşen" value={result.matched_contacts} tone="emerald" />
              <RStat label="Hatalı" value={result.error_count} tone={result.error_count > 0 ? "rose" : ""} />
            </div>
            {result.errors && result.errors.length > 0 && (
              <div className="max-h-56 overflow-auto border border-rose-200 bg-rose-50 p-3">
                <p className="font-semibold text-xs uppercase tracking-widest text-rose-700 mb-2">Hatalar</p>
                <ul className="text-xs space-y-1 text-rose-800">
                  {result.errors.map((e, i) => <li key={i}>Satır {e.row}: {e.message}</li>)}
                </ul>
              </div>
            )}
            <div className="flex justify-end">
              <button
                onClick={onClose}
                className="brutal-btn bg-black text-white px-4 py-2 text-sm"
                data-testid="cheque-import-done"
              >
                Kapat
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RStat({ label, value, tone }) {
  const cls = tone === "emerald" ? "text-emerald-700" : tone === "rose" ? "text-rose-600" : "text-black";
  return (
    <div className="border-2 border-black p-3">
      <p className="label-caps text-[10px]">{label}</p>
      <p className={`font-mono-num text-2xl font-bold mt-1 ${cls}`}>{value ?? 0}</p>
    </div>
  );
}
